import React, { PureComponent } from 'react';
import { Loader } from 'react-loaders';

import { getRequest } from '../../../../api/ApiService';
import Teams from '../../../../api/Teams';
import Auth from '../../../../routes/Auth';

import MarketRanking, { RANK_NAME_TO_NUMBER } from '../../../market-ranking';

// "Round Team Score" step — replaces the previous Overall Score
// bar / Score Trend line chart layout with a per-rank celebration
// screen keyed off the team's `ranking` string from teamgradings.
//
// Data flow:
//   1. Fetch /v2/games/teamgradings/?round=<thisRoundId>&game=<gameId>
//      (no &team= filter) — gives us this team's row PLUS every other
//      team in the cohort. From that one response we get the team's
//      score + ranking and can compute highest / median / average.
//   2. If we're past round 1, also fetch the prior round (team
//      filtered) to compute the round-over-round changePct.
//
// Both calls are wrapped so a single failure doesn't blank the page.
class ResultsAreInStep5 extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      rank: null,
      teamName: '',
      totalTeams: 0,
      score: 0,
      changePct: null,
      cohort: null,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  fetchData = () => {
    const { activeRound, rounds } = this.props;
    const apiUrl = process.env.REACT_APP_API_URL;

    const gameId = Number(Auth.getGameId());
    const team = Teams.currentTeam();
    const teamId = Number(team && team.id);
    const teamName = (team && team.name) || 'Your team';
    const roundNumber = Number(activeRound);
    const thisRound = rounds.find(i => Number(i.number) === roundNumber);
    const thisRoundId = thisRound ? Number(thisRound.id) : 0;
    const prevRound = roundNumber > 1
      ? rounds.find(i => Number(i.number) === roundNumber - 1)
      : null;
    const prevRoundId = prevRound ? Number(prevRound.id) : 0;

    const thisRoundReq = getRequest(
      `${apiUrl}v2/games/teamgradings/?round=${thisRoundId}&game=${gameId}`
    ).catch(() => null);

    const prevRoundReq = prevRoundId
      ? getRequest(
          `${apiUrl}v2/games/teamgradings/?round=${prevRoundId}&game=${gameId}&team=${teamId}`
        ).catch(() => null)
      : Promise.resolve(null);

    // Cohort is the current team's competition (a subset of the
    // game), not the whole game. Teams.teams() already returns the
    // teams in the current team's competition; use those ids to
    // filter the teamgradings response.
    const competitionTeamIds = new Set(
      Teams.teams().map(t => Number(t.id))
    );

    Promise.all([thisRoundReq, prevRoundReq])
      .then(([thisRes, prevRes]) => {
        const allRows = (thisRes && thisRes.body && thisRes.body.results) || [];
        const cohortRows = allRows.filter(r =>
          Number(r.game) === gameId
          && competitionTeamIds.has(Number(r.team))
        );
        const myRow = cohortRows.find(r => Number(r.team) === teamId) || null;

        const score = myRow ? Number(myRow.current_weighted_value) || 0 : 0;
        const rankName = myRow && myRow.ranking ? String(myRow.ranking) : '';
        const rank = RANK_NAME_TO_NUMBER[rankName.toLowerCase()] || null;
        const totalTeams = cohortRows.length;

        // Cohort stats — read directly off the team's own gradings
        // row. The backend already computes highest / median / average
        // at the competition level for student users, so trusting
        // those fields keeps the UI aligned with what's on the wire
        // instead of re-deriving them locally.
        const highest = myRow ? Number(myRow.highest_weighted_value) || 0 : 0;
        const median = myRow ? Number(myRow.median_weighted_value) || 0 : 0;
        const average = myRow ? Number(myRow.average_weighted_value) || 0 : 0;

        // Prior-round score → changePct. Null for round 1 or when
        // the prior score was zero (avoids divide-by-zero).
        let changePct = null;
        if (prevRes && prevRes.body && Array.isArray(prevRes.body.results)) {
          const prevRow = prevRes.body.results.find(r => Number(r.team) === teamId);
          if (prevRow) {
            const prevScore = Number(prevRow.current_weighted_value) || 0;
            if (prevScore > 0) {
              changePct = ((score - prevScore) / prevScore) * 100;
            }
          }
        }

        this.setState({
          loaded: true,
          rank,
          teamName,
          totalTeams,
          score,
          changePct,
          cohort: { highest, median, average },
        });
      })
      .catch(() => {
        this.setState({ loaded: true });
      });
  };

  render() {
    const { loaded, rank, teamName, totalTeams, score, changePct, cohort } = this.state;

    if (!loaded) {
      return <Loader type="ball-scale-ripple" active />;
    }

    return (
      <MarketRanking
        teamName={teamName}
        rank={rank}
        totalTeams={totalTeams}
        score={score}
        changePct={changePct}
        cohort={cohort}
      />
    );
  }
}

export default ResultsAreInStep5;



// WEBPACK FOOTER //
// ./src/components/performance-feedback/pages/results-are-in/step5.js